import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, AlertTriangle, Loader2, Mail } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import InfoCard from "@/components/toggles/InfoCard";

/**
 * EmailVerifyLandingPage (iter174n)
 * Landing für Magic-Link `/verify-email?token=…`.
 * - Ruft POST /api/auth/verify-email {token} auf.
 * - Zeigt Ergebnis (Erfolg / Fehler / abgelaufen) und Navigation.
 * - Refresht Auth-Kontext, damit `user.email_verified` sofort greift.
 */
export default function EmailVerifyLandingPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const token = params.get("token") || "";
  const [state, setState] = useState("pending"); // pending | success | error
  const [error, setError] = useState("");
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) { setState("error"); setError("Kein Token in der URL."); return; }
    (async () => {
      try {
        await api.post("/auth/verify-email", { token });
        setState("success");
        // Refresh user context (email_verified flag updates)
        try { if (typeof refreshUser === "function") await refreshUser(); } catch { /* noop */ }
      } catch (e) {
        setState("error");
        const d = e?.response?.data?.detail;
        setError(typeof d === "string" ? d : (d?.message || formatError(e) || "Verifikation fehlgeschlagen."));
      }
    })();
  }, [token, refreshUser]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4" data-testid="email-verify-landing">
      <div className="max-w-md w-full border border-[#E5E5E5] p-6 bg-white">
        <div className="flex items-center gap-2 mb-4">
          <Mail size={18} className="text-[#0A0A0A]" />
          <h1 className="text-base font-bold text-[#0A0A0A]">E-Mail-Verifizierung</h1>
        </div>

        {state === "pending" && (
          <div className="flex items-center gap-2 text-sm text-[#666]" data-testid="verify-state-pending">
            <Loader2 size={16} className="animate-spin" /> Prüfe deinen Link …
          </div>
        )}

        {state === "success" && (
          <div className="space-y-3" data-testid="verify-state-success">
            <InfoCard variant="success" icon={<CheckCircle size={18} className="text-emerald-600" />}>
              <p className="font-semibold">{t("email_verify.confirmed_title")}</p>
              <p className="mt-0.5">{t("email_verify.confirmed_desc")}</p>
            </InfoCard>
            <div className="flex gap-2">
              <button
                data-testid="verify-goto-security"
                onClick={() => navigate("/profile/security", { replace: true })}
                className="flex-1 h-10 bg-[#0A0A0A] text-white text-sm hover:bg-[#222]"
              >
                Zur Sicherheitsseite
              </button>
              <button
                data-testid="verify-goto-home"
                onClick={() => navigate(user ? "/" : "/login", { replace: true })}
                className="flex-1 h-10 border border-[#E5E5E5] text-sm text-[#0A0A0A] hover:bg-[#F5F5F5]"
              >
                Zur Startseite
              </button>
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="space-y-3" data-testid="verify-state-error">
            <InfoCard variant="danger" icon={<AlertTriangle size={18} className="text-red-600" />}>
              <p className="font-semibold">Verifikation fehlgeschlagen.</p>
              <p className="mt-0.5" data-testid="verify-error-message">{error}</p>
              <p className="mt-2 text-[11px] text-red-800">
                Der Link ist ungültig oder abgelaufen. Fordere in deinem Profil unter
                &bdquo;Sicherheit &amp; Verifikation&ldquo; eine neue Bestätigungs-E-Mail an.
              </p>
            </InfoCard>
            <div className="flex gap-2">
              <Link
                to="/profile/security"
                data-testid="verify-error-security-link"
                className="flex-1 h-10 flex items-center justify-center bg-[#0A0A0A] text-white text-sm hover:bg-[#222]"
              >
                Neue E-Mail anfordern
              </Link>
              <Link
                to="/login"
                data-testid="verify-error-login-link"
                className="flex-1 h-10 flex items-center justify-center border border-[#E5E5E5] text-sm text-[#0A0A0A] hover:bg-[#F5F5F5]"
              >
                Zum Login
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
